import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission";

const PortfolioAddPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assetClassList, setAssetClassList] = useState([]);

  // Helper: map API (flat) -> UI formData (nested)
  const transformApiToFormData = (data) => {
    const toArray = (value, fallback = [""]) => {
      if (Array.isArray(value)) return value.length ? value : fallback;
      if (value === undefined || value === null || value === "") return fallback;
      return [value];
    };

    const parseJsonArray = (value, fallback = []) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : fallback;
        } catch (err) {
          return fallback;
        }
      }
      return fallback;
    };

    // Build rows from parallel arrays if present
    const buildRows = (categories, indices) => {
      const cat = Array.isArray(categories) ? categories : [];
      const ind = Array.isArray(indices) ? indices : [];
      const len = Math.max(cat.length, ind.length);
      if (!len) return [["", ""]];
      const rows = [];
      for (let i = 0; i < len; i += 1) {
        rows.push([cat[i] || "", ind[i] || ""]);
      }
      return rows;
    };

    const normalizeRows = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return [["", ""]];
      return rows.map((r) => (Array.isArray(r) ? [r[0] || "", r[1] || ""] : ["", ""]));
    };

    return {
      // Methodology
      defining_the_universe: data.defining_the_universe || data?.methodology?.defining_the_universe || "",
      research: data.research || data?.methodology?.research || "",
      constituent_screening: data.constituent_screening || data?.methodology?.constituent_screening || "",
      weighting: data.weighting || data?.methodology?.weighting || "",
      rebalance: data.rebalance || data?.methodology?.rebalance || "",
      asset_allocation: data.asset_allocation || data?.methodology?.asset_allocation || "",
      video_section: {
        video_link: data.video_link || "",
        video_time: data.video_time || "",
      },
      philosophy_section: {
        heading: data.p_heading || "",
        description: data.p_description || "",
        archetype: data.p_archetype || "",
        shortDescription: data.p_sdescription || "",
      },
      strategy_section: {
        heading: data.s_heading || "",
        description: data.s_description || "",
        box1: {
          heading: data.sb1_heading || "",
          description: data.sb1_description || "",
        },
        box2: {
          heading: data.sb2_heading || "",
          description: data.sb2_description || "",
        },
        box3: {
          heading: data.sb3_heading || "",
          description: data.sb3_description || "",
        },
      },
      basket_allocation: {
        description: data.a_description || "",
      },
      built_for: {
        heading: data.b_heading || data?.built_for?.heading || "",
        description: data.b_description || data?.built_for?.description || "",
        points: parseJsonArray(data.b_points || data?.built_for?.points, [""])
          .map((p) => (typeof p === "string" ? p : "")),
      },
      tagline: data.tagline || "",
      investment_objective: data.investment_objective || "",
      sector_universe: data.sector_universe || "",
      volatility_risk: data.volatility_risk || "",
      anchor_tagline: data.anchor_tagline || "",
      anchor_description: data.anchor_description || "",

      // Portfolio Rationale
      rationale: {
        title: data.rationale_title || data?.rationale?.title || "",
        description: data.rationale_description || data?.rationale?.description || "",
        conclusion: data.rationale_conclusion || data?.rationale?.conclusion || "",
        keyHighlights: {
          title: data.rationale_sub_title || data?.rationale?.keyHighlights?.title || "",
          points: toArray(
            data.rationale_points || data?.rationale?.keyHighlights?.points,
            [""]
          ).map((p) => (typeof p === "string" ? p : "")),
        },
      },

      // Definitions and Disclosures
      cagr: {
        content: toArray(data.cagr_content || data?.cagr?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      volatility: {
        content: toArray(data.volatility_content || data?.volatility?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      investment: {
        content: toArray(data.investment_content || data?.investment?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.investment_list || data?.investment?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      asset_class: {
        content: toArray(data.asset_content || data?.asset_class?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.asset_list || data?.asset_class?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      rebalance_portfolio: {
        content: toArray(data.rebalance_content || data?.rebalance_portfolio?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      categorisation_of_constituents: {
        content: toArray(data.categorisation_content || data?.categorisation_of_constituents?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.categorisation_list || data?.categorisation_of_constituents?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        additionalContent: toArray(
          data.categorisation_additional_content || data?.categorisation_of_constituents?.additionalContent,
          [""]
        ).map((v) => (typeof v === "string" ? v : "")),
      },
      holding_distribution: {
        content: toArray(data.holding_distribution_content || data?.holding_distribution?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
      },
      determination_of_comparable_index: {
        content: toArray(data.determination_content || data?.determination_of_comparable_index?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.determination_list || data?.determination_of_comparable_index?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        additionalContent: toArray(
          data.determination_additional_content || data?.determination_of_comparable_index?.additionalContent,
          [""]
        ).map((v) => (typeof v === "string" ? v : "")),
      },
      comparison_of_performance: {
        content: toArray(data.comparison_content || data?.comparison_of_performance?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.comparison_list || data?.comparison_of_performance?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        rows: Array.isArray(data?.comparison_of_performance?.rows)
          ? normalizeRows(data.comparison_of_performance.rows)
          : buildRows(data.market_cap_category, data.comparable_index),
      },
      general_investment_disclosure: {
        content: toArray(
          data.investment_disclosure_content || data?.general_investment_disclosure?.content,
          [""]
        ).map((v) => (typeof v === "string" ? v : "")),
      },
      risk_disclosure: {
        content: toArray(data.risk_disclosure_content || data?.risk_disclosure?.content, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        list: toArray(data.risk_disclosure_list || data?.risk_disclosure?.list, [""])
          .map((v) => (typeof v === "string" ? v : "")),
        additionalList: (() => {
          // First try to read from risk_disclosure_additional_list (flat array of strings)
          const flat = toArray(data.risk_disclosure_additional_list, []);
          if (flat.length && typeof flat[0] === "string") {
            return [
              {
                title: "",
                content: flat.map((v) => (typeof v === "string" ? v : "")),
              },
            ];
          }

          // If risk_disclosure_additional_list contains objects with title/content
          if (Array.isArray(data.risk_disclosure_additional_list) && data.risk_disclosure_additional_list.length > 0) {
            return data.risk_disclosure_additional_list.map((item) => ({
              title: item?.title || "",
              content: Array.isArray(item?.content) ? item.content : [""],
            }));
          }

          // Else try to read nested structure if present
          const nested = data?.risk_disclosure?.additionalList;
          if (Array.isArray(nested) && nested.length) return nested;
          return [{ title: "", content: [""] }];
        })(),
      },
    };
  };

  // Helper: map UI formData (nested) -> API (flat)
  const transformFormDataToApi = (formDataArg) => {
    const rows = Array.isArray(formDataArg?.comparison_of_performance?.rows)
      ? formDataArg.comparison_of_performance.rows
      : [["", ""]];
    const marketCapCategory = rows.map((r) => (Array.isArray(r) ? r[0] || "" : ""));
    const comparableIndex = rows.map((r) => (Array.isArray(r) ? r[1] || "" : ""));

    // Handle risk additional sections - keep as objects if they have title/content structure
    const additionalSections = Array.isArray(formDataArg?.risk_disclosure?.additionalList)
      ? formDataArg.risk_disclosure.additionalList
      : [];

    // Check if sections have title/content structure
    const hasTitleContentStructure = additionalSections.length > 0 &&
      additionalSections.some(section => section?.title && Array.isArray(section?.content));

    let riskAdditionalData;
    if (hasTitleContentStructure) {
      // Keep as array of objects with title and content
      riskAdditionalData = additionalSections.map(section => ({
        title: section?.title || "",
        content: Array.isArray(section?.content) ? section.content.filter(Boolean) : []
      }));
    } else {
      // Flatten into array of strings (legacy format)
      riskAdditionalData = additionalSections.flatMap((section) => {
        const title = section?.title ? `${section.title}: ` : "";
        const contentArr = Array.isArray(section?.content) ? section.content : [];
        return contentArr.filter(Boolean).map((c) => `${title}${c}`.trim());
      });
    }

    return {
      defining_the_universe: formDataArg.defining_the_universe,
      research: formDataArg.research,
      constituent_screening: formDataArg.constituent_screening,
      weighting: formDataArg.weighting,
      rebalance: formDataArg.rebalance,
      asset_allocation: formDataArg.asset_allocation,
      video_link: formDataArg?.video_section?.video_link || "",
      video_time: formDataArg?.video_section?.video_time || "",
      p_heading: formDataArg?.philosophy_section?.heading || "",
      p_description: formDataArg?.philosophy_section?.description || "",
      p_archetype: formDataArg?.philosophy_section?.archetype || "",
      p_sdescription: formDataArg?.philosophy_section?.shortDescription || "",
      s_heading: formDataArg?.strategy_section?.heading || "",
      s_description: formDataArg?.strategy_section?.description || "",
      sb1_heading: formDataArg?.strategy_section?.box1?.heading || "",
      sb1_description: formDataArg?.strategy_section?.box1?.description || "",
      sb2_heading: formDataArg?.strategy_section?.box2?.heading || "",
      sb2_description: formDataArg?.strategy_section?.box2?.description || "",
      sb3_heading: formDataArg?.strategy_section?.box3?.heading || "",
      sb3_description: formDataArg?.strategy_section?.box3?.description || "",
      a_description: formDataArg?.basket_allocation?.description || "",
      b_heading: formDataArg?.built_for?.heading || "",
      b_description: formDataArg?.built_for?.description || "",
      b_points: Array.isArray(formDataArg?.built_for?.points)
        ? formDataArg.built_for.points
        : [],

      rationale_title: formDataArg?.rationale?.title || "",
      rationale_sub_title: formDataArg?.rationale?.keyHighlights?.title || "",
      rationale_description: formDataArg?.rationale?.description || "",
      rationale_points: Array.isArray(formDataArg?.rationale?.keyHighlights?.points)
        ? formDataArg.rationale.keyHighlights.points
        : [],
      rationale_conclusion: formDataArg?.rationale?.conclusion || "",
      tagline: formDataArg.tagline || "",
      investment_objective: formDataArg.investment_objective || "",
      sector_universe: formDataArg.sector_universe || "",
      volatility_risk: formDataArg.volatility_risk || "",
      anchor_tagline: formDataArg.anchor_tagline || "",
      anchor_description: formDataArg.anchor_description || "",

      cagr_content: Array.isArray(formDataArg?.cagr?.content) ? formDataArg.cagr.content : [],
      volatility_content: Array.isArray(formDataArg?.volatility?.content)
        ? formDataArg.volatility.content
        : [],
      investment_content: Array.isArray(formDataArg?.investment?.content)
        ? formDataArg.investment.content
        : [],
      investment_list: Array.isArray(formDataArg?.investment?.list)
        ? formDataArg.investment.list
        : [],
      asset_content: Array.isArray(formDataArg?.asset_class?.content)
        ? formDataArg.asset_class.content
        : [],
      asset_list: Array.isArray(formDataArg?.asset_class?.list)
        ? formDataArg.asset_class.list
        : [],
      rebalance_content: Array.isArray(formDataArg?.rebalance_portfolio?.content)
        ? formDataArg.rebalance_portfolio.content
        : [],
      categorisation_content: Array.isArray(formDataArg?.categorisation_of_constituents?.content)
        ? formDataArg.categorisation_of_constituents.content
        : [],
      categorisation_list: Array.isArray(formDataArg?.categorisation_of_constituents?.list)
        ? formDataArg.categorisation_of_constituents.list
        : [],
      categorisation_additional_content: Array.isArray(
        formDataArg?.categorisation_of_constituents?.additionalContent
      )
        ? formDataArg.categorisation_of_constituents.additionalContent
        : [],
      holding_distribution_content: Array.isArray(formDataArg?.holding_distribution?.content)
        ? formDataArg.holding_distribution.content
        : [],
      determination_content: Array.isArray(
        formDataArg?.determination_of_comparable_index?.content
      )
        ? formDataArg.determination_of_comparable_index.content
        : [],
      determination_list: Array.isArray(formDataArg?.determination_of_comparable_index?.list)
        ? formDataArg.determination_of_comparable_index.list
        : [],
      determination_additional_content: Array.isArray(
        formDataArg?.determination_of_comparable_index?.additionalContent
      )
        ? formDataArg.determination_of_comparable_index.additionalContent
        : [],
      comparison_content: Array.isArray(formDataArg?.comparison_of_performance?.content)
        ? formDataArg.comparison_of_performance.content
        : [],
      comparison_list: Array.isArray(formDataArg?.comparison_of_performance?.list)
        ? formDataArg.comparison_of_performance.list
        : [],
      market_cap_category: marketCapCategory,
      comparable_index: comparableIndex,
      investment_disclosure_content: Array.isArray(
        formDataArg?.general_investment_disclosure?.content
      )
        ? formDataArg.general_investment_disclosure.content
        : [],
      risk_disclosure_content: Array.isArray(formDataArg?.risk_disclosure?.content)
        ? formDataArg.risk_disclosure.content
        : [],
      risk_disclosure_list: Array.isArray(formDataArg?.risk_disclosure?.list)
        ? formDataArg.risk_disclosure.list
        : [],
      risk_disclosure_additional_list: riskAdditionalData,
    };
  };

  // Initial state structure
  const [formData, setFormData] = useState({
    tagline: "",
    investment_objective: "",
    sector_universe: "",
    volatility_risk: "",
    anchor_tagline: "",
    anchor_description: "",
    // Methodology
    defining_the_universe: "",
    research: "",
    constituent_screening: "",
    weighting: "",
    rebalance: "",
    asset_allocation: "",
    video_section: {
      video_link: "",
      video_time: "",
    },
    philosophy_section: {
      heading: "",
      description: "",
      archetype: "",
      shortDescription: "",
    },
    strategy_section: {
      heading: "",
      description: "",
      box1: {
        heading: "",
        description: "",
      },
      box2: {
        heading: "",
        description: "",
      },
      box3: {
        heading: "",
        description: "",
      },
    },
    basket_allocation: {
      description: "",
    },
    built_for: {
      heading: "",
      description: "",
      points: [""],
    },

    // Portfolio Rationale
    rationale: {
      title: "",
      description: "",
      conclusion: "",
      keyHighlights: {
        title: "",
        points: [""],
      },
    },

    // Definitions and Disclosures
    cagr: { content: [""] },
    volatility: { content: [""] },
    investment: {
      content: [""],
      list: [""],
    },
    asset_class: {
      content: [""],
      list: [""],
    },
    rebalance_portfolio: { content: [""] },
    categorisation_of_constituents: {
      content: [""],
      list: [""],
      additionalContent: [""],
    },
    holding_distribution: { content: [""] },
    determination_of_comparable_index: {
      content: [""],
      list: [""],
      additionalContent: [""],
    },
    comparison_of_performance: {
      content: [""],
      list: [""],
      rows: [["", ""]],
    },
    general_investment_disclosure: { content: [""] },
    risk_disclosure: {
      content: [""],
      list: [""],
      additionalList: [
        {
          title: "",
          content: [""],
        },
      ],
    },
  });

  // Temporary state for the active input in tag field
  const [tagInput, setTagInput] = useState("");

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = tagInput.trim().replace(/,/g, "");
      if (val) {
        const currentTags = formData.sector_universe
          ? formData.sector_universe.split(",").map((t) => t.trim()).filter(Boolean)
          : [];
        if (!currentTags.includes(val)) {
          const newTags = [...currentTags, val].join(", ");
          handleInputChange("sector_universe", null, newTags);
        }
      }
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = formData.sector_universe
      ? formData.sector_universe.split(",").map((t) => t.trim()).filter(Boolean)
      : [];
    const newTags = currentTags.filter((t) => t !== tagToRemove).join(", ");
    handleInputChange("sector_universe", null, newTags);
  };

  // ReactQuill modules configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  // ReactQuill formats
  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_URL}service/add-page`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        });

        // Ensure all required fields are present in the response
        const data = response.data;
        if (data && Array.isArray(data.assetClassList)) {
          setAssetClassList(data.assetClassList);
        }
        setFormData(transformApiToFormData(data));

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    const fetchAssetClasses = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          `${API_URL}notification/dropdown/asset-class`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data && Array.isArray(response.data)) {
          setAssetClassList(response.data);
        }
      } catch (error) {
        console.error("Error fetching asset classes:", error);
      }
    };

    fetchData();
    fetchAssetClasses();
  }, [id]);

  // Handle input changes
  const handleInputChange = (
    section,
    field,
    value,
    index = null,
    subField = null
  ) => {
    setFormData((prevData) => {
      const newData = { ...prevData };

      if (index !== null) {
        if (subField !== null) {
          // For nested arrays like additionalList
          newData[section][field][index][subField] = value;
        } else {
          // For simple arrays
          newData[section][field][index] = value;
        }
      } else if (field) {
        // For nested objects
        if (section.includes(".")) {
          const parts = section.split(".");
          if (parts.length === 2) {
            newData[parts[0]][parts[1]][field] = value;
          } else if (parts.length === 3) {
            newData[parts[0]][parts[1]][parts[2]][field] = value;
          }
        } else {
          newData[section][field] = value;
        }
      } else {
        // For top-level fields
        newData[section] = value;
      }

      return newData;
    });
  };

  // Add item to array field
  const addArrayItem = (section, field, defaultValue = "") => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: [...prevData[section][field], defaultValue],
      },
    }));
  };

  // Remove item from array field
  const removeArrayItem = (section, field, index) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: prevData[section][field].filter((_, i) => i !== index),
      },
    }));
  };

  // Add row to comparison table
  const addComparisonRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      comparison_of_performance: {
        ...prevData.comparison_of_performance,
        rows: [...prevData.comparison_of_performance.rows, ["", ""]],
      },
    }));
  };

  // Remove row from comparison table
  const removeComparisonRow = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      comparison_of_performance: {
        ...prevData.comparison_of_performance,
        rows: prevData.comparison_of_performance.rows.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Add section to additionalList
  const addAdditionalListSection = () => {
    setFormData((prevData) => ({
      ...prevData,
      risk_disclosure: {
        ...prevData.risk_disclosure,
        additionalList: [
          ...prevData.risk_disclosure.additionalList,
          { title: "", content: [""] },
        ],
      },
    }));
  };

  // Remove section from additionalList
  const removeAdditionalListSection = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      risk_disclosure: {
        ...prevData.risk_disclosure,
        additionalList: prevData.risk_disclosure.additionalList.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Add content to additionalList section
  const addAdditionalListContent = (sectionIndex) => {
    setFormData((prevData) => {
      const updatedAdditionalList = prevData.risk_disclosure.additionalList.map(
        (section, idx) => {
          if (idx !== sectionIndex) return section;
          const currentContentArray = Array.isArray(section.content)
            ? section.content
            : [];
          return {
            ...section,
            content: [...currentContentArray, ""],
          };
        }
      );

      return {
        ...prevData,
        risk_disclosure: {
          ...prevData.risk_disclosure,
          additionalList: updatedAdditionalList,
        },
      };
    });
  };

  // Remove content from additionalList section
  const removeAdditionalListContent = (sectionIndex, contentIndex) => {
    setFormData((prevData) => {
      const newAdditionalList = [...prevData.risk_disclosure.additionalList];
      newAdditionalList[sectionIndex].content = newAdditionalList[
        sectionIndex
      ].content.filter((_, i) => i !== contentIndex);

      return {
        ...prevData,
        risk_disclosure: {
          ...prevData.risk_disclosure,
          additionalList: newAdditionalList,
        },
      };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");
      const payload = transformFormDataToApi(formData);
      await axios.post(`${API_URL}service/portfolio-add/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/research/Portfolio");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving data. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="p-4" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1" style={{ fontWeight: 700, color: "#1e293b" }}>Edit Portfolio Services</h2>
            <p className="text-muted mb-0">Manage methodology, philosophy, strategy, and disclosures for this service.</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="stdform">
          {/* Website Heading */}
          <div className="d-flex align-items-center my-4">
            <div style={{ flex: 1, height: "2px", backgroundColor: "#cbd5e1" }}></div>
            <h4 className="px-3 mb-0" style={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "1.5px", fontSize: "1.2rem" }}>
              Website
            </h4>
            <div style={{ flex: 1, height: "2px", backgroundColor: "#cbd5e1" }}></div>
          </div>

          {/* Methodology Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Methodology
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Defining The Universe</label>
                    <ReactQuill
                      value={formData.defining_the_universe}
                      onChange={(value) =>
                        handleInputChange("defining_the_universe", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Research</label>
                    <ReactQuill
                      value={formData.research}
                      onChange={(value) =>
                        handleInputChange("research", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Constituent Screening</label>
                    <ReactQuill
                      value={formData.constituent_screening}
                      onChange={(value) =>
                        handleInputChange("constituent_screening", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Weighting</label>
                    <ReactQuill
                      value={formData.weighting}
                      onChange={(value) =>
                        handleInputChange("weighting", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Rebalance</label>
                    <ReactQuill
                      value={formData.rebalance}
                      onChange={(value) =>
                        handleInputChange("rebalance", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Asset Allocation</label>
                    <ReactQuill
                      value={formData.asset_allocation}
                      onChange={(value) =>
                        handleInputChange("asset_allocation", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Philosophy & Video Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Philosophy & Video
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              {/* Video Fields */}
              <h6 className="font-weight-bold mb-3 text-secondary" style={{ borderBottom: "1px solid #eef2f5", paddingBottom: "8px" }}>Video Presentation</h6>
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Video Link</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.video_section.video_link}
                      onChange={(e) =>
                        handleInputChange(
                          "video_section",
                          "video_link",
                          e.target.value
                        )
                      }
                      placeholder="Enter Video Link"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Video Time</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.video_section.video_time}
                      onChange={(e) =>
                        handleInputChange(
                          "video_section",
                          "video_time",
                          e.target.value
                        )
                      }
                      placeholder="Enter Video Time"
                    />
                  </div>
                </div>
              </div>

              {/* Philosophy Fields */}
              <h6 className="font-weight-bold mb-3 text-secondary" style={{ borderBottom: "1px solid #eef2f5", paddingBottom: "8px" }}>Philosophy Details</h6>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Heading</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.philosophy_section.heading}
                      onChange={(e) =>
                        handleInputChange(
                          "philosophy_section",
                          "heading",
                          e.target.value
                        )
                      }
                      placeholder="Enter Heading"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Archetype</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.philosophy_section.archetype}
                      onChange={(e) =>
                        handleInputChange(
                          "philosophy_section",
                          "archetype",
                          e.target.value
                        )
                      }
                      placeholder="Enter Archetype"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Description</label>
                    <ReactQuill
                      value={formData.philosophy_section.description}
                      onChange={(value) =>
                        handleInputChange(
                          "philosophy_section",
                          "description",
                          value
                        )
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-0">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Short Description</label>
                    <textarea
                      className="form-control"
                      value={formData.philosophy_section.shortDescription}
                      onChange={(e) =>
                        handleInputChange(
                          "philosophy_section",
                          "shortDescription",
                          e.target.value
                        )
                      }
                      placeholder="Enter Short Description"
                      rows="4"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Strategy Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Strategy Details
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Strategy Heading</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.strategy_section.heading}
                      onChange={(e) =>
                        handleInputChange(
                          "strategy_section",
                          "heading",
                          e.target.value
                        )
                      }
                      placeholder="Enter Heading"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Description</label>
                    <ReactQuill
                      value={formData.strategy_section.description}
                      onChange={(value) =>
                        handleInputChange(
                          "strategy_section",
                          "description",
                          value
                        )
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              {/* Sub-box layout */}
              <h6 className="font-weight-bold mb-3 text-secondary" style={{ borderBottom: "1px solid #eef2f5", paddingBottom: "8px" }}>Key Strategy Pillars / Pillars Box</h6>
              
              {/* Box 1 */}
              <div className="p-3 mb-3 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label className="font-weight-bold text-dark mb-2">Box 1 Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.strategy_section.box1.heading}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box1",
                            "heading",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 1 Heading"
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Box 1 Description</label>
                      <textarea
                        className="form-control"
                        value={formData.strategy_section.box1.description}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box1",
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 1 Description"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Box 2 */}
              <div className="p-3 mb-3 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label className="font-weight-bold text-dark mb-2">Box 2 Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.strategy_section.box2.heading}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box2",
                            "heading",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 2 Heading"
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Box 2 Description</label>
                      <textarea
                        className="form-control"
                        value={formData.strategy_section.box2.description}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box2",
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 2 Description"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Box 3 */}
              <div className="p-3 mb-0 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label className="font-weight-bold text-dark mb-2">Box 3 Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.strategy_section.box3.heading}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box3",
                            "heading",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 3 Heading"
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Box 3 Description</label>
                      <textarea
                        className="form-control"
                        value={formData.strategy_section.box3.description}
                        onChange={(e) =>
                          handleInputChange(
                            "strategy_section.box3",
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Enter Box 3 Description"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Basket Allocation & Target Audience Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Basket Allocation & Target Audience
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              {/* Basket Allocation */}
              <h6 className="font-weight-bold mb-3 text-secondary" style={{ borderBottom: "1px solid #eef2f5", paddingBottom: "8px" }}>Basket Allocation</h6>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.basket_allocation.description}
                      onChange={(e) =>
                        handleInputChange(
                          "basket_allocation",
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="Enter Allocation Description"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              {/* Built For */}
              <h6 className="font-weight-bold mb-3 text-secondary" style={{ borderBottom: "1px solid #eef2f5", paddingBottom: "8px" }}>Target Audience (Built For)</h6>
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Heading</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.built_for.heading}
                      onChange={(e) =>
                        handleInputChange(
                          "built_for",
                          "heading",
                          e.target.value
                        )
                      }
                      placeholder="Enter Heading"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.built_for.description}
                      onChange={(e) =>
                        handleInputChange(
                          "built_for",
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="Enter Description"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-0">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Target Audience Points</label>
                    <div id="built-for-points-container" className="mb-2">
                      {formData.built_for.points.map((point, index) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                          <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                          <input
                            type="text"
                            className="form-control"
                            value={point}
                            onChange={(e) =>
                              handleInputChange(
                                "built_for",
                                "points",
                                e.target.value,
                                index
                              )
                            }
                            placeholder="Enter Point"
                          />
                          {index > 0 && (
                            <button
                              type="button"
                              className="btn btn-danger remove-point shadow-none ml-2"
                              onClick={() => removeArrayItem("built_for", "points", index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm shadow-none mt-1"
                      onClick={() => addArrayItem("built_for", "points", "")}
                    >
                      Add Point
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Factsheet Heading */}
          <div className="d-flex align-items-center my-4">
            <div style={{ flex: 1, height: "2px", backgroundColor: "#cbd5e1" }}></div>
            <h4 className="px-3 mb-0" style={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "1.5px", fontSize: "1.2rem" }}>
              Factsheet
            </h4>
            <div style={{ flex: 1, height: "2px", backgroundColor: "#cbd5e1" }}></div>
          </div>

          {/* Factsheet Information Data Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Information Data
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Factsheet Tagline</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.tagline}
                      onChange={(e) =>
                        handleInputChange("tagline", null, e.target.value)
                      }
                      placeholder="Enter Factsheet Tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Investment Objective</label>
                    <textarea
                      className="form-control"
                      value={formData.investment_objective}
                      onChange={(e) =>
                        handleInputChange("investment_objective", null, e.target.value)
                      }
                      placeholder="Enter Investment Objective"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Sector Universe</label>
                    <div className="d-flex flex-wrap align-items-center p-2 border rounded bg-white" style={{ borderColor: "#ced4da" }}>
                      {formData.sector_universe && formData.sector_universe.split(",").map((t) => t.trim()).filter(Boolean).map((tag, idx) => (
                        <span key={idx} className="badge badge-primary mr-2 mb-2 p-2 d-inline-flex align-items-center" style={{ fontSize: "0.85rem", backgroundColor: "#3b82f6", borderRadius: "4px", color: "#fff" }}>
                          {tag}
                          <span 
                            style={{ cursor: "pointer", marginLeft: "6px", fontWeight: "bold" }} 
                            onClick={() => removeTag(tag)}
                          >
                            &times;
                          </span>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="border-0 flex-grow-1 mb-2 p-1"
                        style={{ outline: "none", minWidth: "150px" }}
                        placeholder="Press Enter or comma to add sector tags..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        onBlur={() => {
                          const val = tagInput.trim().replace(/,/g, "");
                          if (val) {
                            const currentTags = formData.sector_universe
                              ? formData.sector_universe.split(",").map((t) => t.trim()).filter(Boolean)
                              : [];
                            if (!currentTags.includes(val)) {
                              const newTags = [...currentTags, val].join(", ");
                              handleInputChange("sector_universe", null, newTags);
                            }
                          }
                          setTagInput("");
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mb-0">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Volatility and Risk Profile</label>
                    <ReactQuill
                      value={formData.volatility_risk}
                      onChange={(value) =>
                        handleInputChange("volatility_risk", null, value)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Philosophy Anchor Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Philosophy Anchor
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Anchor Tagline</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.anchor_tagline}
                      onChange={(e) =>
                        handleInputChange("anchor_tagline", null, e.target.value)
                      }
                      placeholder="Enter Anchor Tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-0">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Anchor Description</label>
                    <textarea
                      className="form-control"
                      value={formData.anchor_description}
                      onChange={(e) =>
                        handleInputChange("anchor_description", null, e.target.value)
                      }
                      placeholder="Enter Anchor Description"
                      rows="4"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Rationale Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Portfolio Rationale
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Rationale Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.rationale.title}
                      onChange={(e) =>
                        handleInputChange("rationale", "title", e.target.value)
                      }
                      placeholder="Enter Rationale Title"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Key Highlights Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.rationale.keyHighlights.title}
                      onChange={(e) =>
                        handleInputChange(
                          "rationale.keyHighlights",
                          "title",
                          e.target.value
                        )
                      }
                      placeholder="Enter Key Highlights Title"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.rationale.description}
                      onChange={(e) =>
                        handleInputChange(
                          "rationale",
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="Enter Rationale Description"
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="font-weight-bold text-dark mb-2">Conclusion</label>
                    <textarea
                      className="form-control"
                      value={formData.rationale.conclusion}
                      onChange={(e) =>
                        handleInputChange(
                          "rationale",
                          "conclusion",
                          e.target.value
                        )
                      }
                      placeholder="Enter Conclusion"
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-dark mb-2">Key Highlight Points</label>
                    <div id="points-container" className="mb-2">
                      {formData.rationale.keyHighlights.points.map(
                        (point, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <input
                              type="text"
                              className="form-control"
                              value={point}
                              onChange={(e) => {
                                const newPoints = [
                                  ...formData.rationale.keyHighlights.points,
                                ];
                                newPoints[index] = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  rationale: {
                                    ...prev.rationale,
                                    keyHighlights: {
                                      ...prev.rationale.keyHighlights,
                                      points: newPoints,
                                    },
                                  },
                                }));
                              }}
                              placeholder="Enter Point"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-point shadow-none ml-2"
                                onClick={() => {
                                  const newPoints =
                                    formData.rationale.keyHighlights.points.filter(
                                      (_, i) => i !== index
                                    );
                                  setFormData((prev) => ({
                                    ...prev,
                                    rationale: {
                                      ...prev.rationale,
                                      keyHighlights: {
                                        ...prev.rationale.keyHighlights,
                                        points: newPoints,
                                      },
                                    },
                                  }));
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm shadow-none mt-1"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          rationale: {
                            ...prev.rationale,
                            keyHighlights: {
                              ...prev.rationale.keyHighlights,
                              points: [
                                ...prev.rationale.keyHighlights.points,
                                "",
                              ],
                            },
                          },
                        }));
                      }}
                    >
                      Add Point
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Definitions & Disclosures Section */}
          <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #eef2f5" }}>
            <div className="card-header py-3" style={{ backgroundColor: "#2b3d52", color: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
              <h5 className="card-title mb-0 font-weight-bold" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                6. Definitions and Disclosures
              </h5>
            </div>
            <div className="card-body bg-white" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", paddingLeft: "10px", paddingRight: "10px", paddingTop: "20px", paddingBottom: "20px" }}>
              
              {/* CAGR Content */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">CAGR Content</label>
                      <div id="cagr_content_container" className="mb-2">
                        {formData.cagr.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("cagr", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("cagr", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("cagr", "content", "")}
                      >
                        Add CAGR Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Volatility Content */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Volatility Label Content</label>
                      <div id="volatility_content_container" className="mb-2">
                        {formData.volatility.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("volatility", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("volatility", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("volatility", "content", "")}
                      >
                        Add Volatility Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Content */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Investment Content</label>
                      <div id="investment_content_container" className="mb-2">
                        {formData.investment.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("investment", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("investment", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("investment", "content", "")}
                      >
                        Add Investment Content
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Investment List</label>
                      <div id="investment_list_container" className="mb-2">
                        {formData.investment.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("investment", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("investment", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("investment", "list", "")}
                      >
                        Add Investment List
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Asset Class */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Asset Content</label>
                      <div id="asset_content_container" className="mb-2">
                        {formData.asset_class.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("asset_class", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("asset_class", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("asset_class", "content", "")}
                      >
                        Add Asset Content
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Asset List</label>
                      <div id="asset_list_container" className="mb-2">
                        {formData.asset_class.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("asset_class", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("asset_class", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("asset_class", "list", "")}
                      >
                        Add Asset List
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rebalance Portfolio */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Rebalance Content</label>
                      <div id="rebalance_content_container" className="mb-2">
                        {formData.rebalance_portfolio.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("rebalance_portfolio", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("rebalance_portfolio", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("rebalance_portfolio", "content", "")}
                      >
                        Add Rebalance Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categorisation */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Categorisation Content</label>
                      <div id="categorisation_content_container" className="mb-2">
                        {formData.categorisation_of_constituents.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("categorisation_of_constituents", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("categorisation_of_constituents", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("categorisation_of_constituents", "content", "")}
                      >
                        Add Categorisation Content
                      </button>
                    </div>

                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Categorisation List</label>
                      <div id="categorisation_list_container" className="mb-2">
                        {formData.categorisation_of_constituents.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("categorisation_of_constituents", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("categorisation_of_constituents", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("categorisation_of_constituents", "list", "")}
                      >
                        Add Categorisation List
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Categorisation Additional Content</label>
                      <div id="categorisation_additional_content_container" className="mb-2">
                        {formData.categorisation_of_constituents.additionalContent.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("categorisation_of_constituents", "additionalContent", e.target.value, index)
                              }
                              placeholder="Enter Additional Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("categorisation_of_constituents", "additionalContent", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("categorisation_of_constituents", "additionalContent", "")}
                      >
                        Add Categorisation Additional Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Holding Distribution */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Holding Distribution Content</label>
                      <div id="holding_distribution_content_container" className="mb-2">
                        {formData.holding_distribution.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("holding_distribution", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("holding_distribution", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("holding_distribution", "content", "")}
                      >
                        Add Holding Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Determination of Comparable Index */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Determination Content</label>
                      <div id="determination_content_container" className="mb-2">
                        {formData.determination_of_comparable_index.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("determination_of_comparable_index", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("determination_of_comparable_index", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("determination_of_comparable_index", "content", "")}
                      >
                        Add Determination Content
                      </button>
                    </div>

                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Determination List</label>
                      <div id="determination_list_container" className="mb-2">
                        {formData.determination_of_comparable_index.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("determination_of_comparable_index", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("determination_of_comparable_index", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("determination_of_comparable_index", "list", "")}
                      >
                        Add Determination List
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Determination Additional Content</label>
                      <div id="determination_additional_content_container" className="mb-2">
                        {formData.determination_of_comparable_index.additionalContent.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("determination_of_comparable_index", "additionalContent", e.target.value, index)
                              }
                              placeholder="Enter Additional Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("determination_of_comparable_index", "additionalContent", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("determination_of_comparable_index", "additionalContent", "")}
                      >
                        Add Determination Additional Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison of Performance */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Comparison Content</label>
                      <div id="comparison_content_container" className="mb-2">
                        {formData.comparison_of_performance.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("comparison_of_performance", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("comparison_of_performance", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("comparison_of_performance", "content", "")}
                      >
                        Add Comparison Content
                      </button>
                    </div>

                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Comparison List</label>
                      <div id="comparison_list_container" className="mb-2">
                        {formData.comparison_of_performance.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("comparison_of_performance", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="2"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("comparison_of_performance", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("comparison_of_performance", "list", "")}
                      >
                        Add Comparison List
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Market Cap Table</label>
                      <table className="table table-bordered bg-white shadow-sm mb-3" id="market-cap-table">
                        <thead className="thead-light">
                          <tr>
                            <th>Market Cap Category</th>
                            <th>Comparable Index</th>
                            <th width="80" className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.comparison_of_performance.rows.map((row, index) => (
                            <tr key={index}>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={row[0]}
                                  onChange={(e) => {
                                    const newRows = [...formData.comparison_of_performance.rows];
                                    newRows[index][0] = e.target.value;
                                    setFormData((prev) => ({
                                      ...prev,
                                      comparison_of_performance: {
                                        ...prev.comparison_of_performance,
                                        rows: newRows,
                                      },
                                    }));
                                  }}
                                  placeholder="Enter Category"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={row[1]}
                                  onChange={(e) => {
                                    const newRows = [...formData.comparison_of_performance.rows];
                                    newRows[index][1] = e.target.value;
                                    setFormData((prev) => ({
                                      ...prev,
                                      comparison_of_performance: {
                                        ...prev.comparison_of_performance,
                                        rows: newRows,
                                      },
                                    }));
                                  }}
                                  placeholder="Enter Index"
                                />
                              </td>
                              <td className="text-center align-middle">
                                {index > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => removeComparisonRow(index)}
                                  >
                                    Remove
                                  </button>
                                ) : (
                                  <span className="text-muted small">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        type="button"
                        id="add-row"
                        className="btn btn-success btn-sm"
                        onClick={addComparisonRow}
                      >
                        Add Row
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Investment Disclosure */}
              <div className="p-3 mb-4 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">General Investment Disclosure Content</label>
                      <div id="investment_disclosure_content_container" className="mb-2">
                        {formData.general_investment_disclosure.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("general_investment_disclosure", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="3"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() =>
                                  removeArrayItem("general_investment_disclosure", "content", index)
                                }
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("general_investment_disclosure", "content", "")}
                      >
                        Add Investment Disclosure Content
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Disclosure */}
              <div className="p-3 mb-0 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Risk Disclosure Content</label>
                      <div id="risk_disclosure_content_container" className="mb-2">
                        {formData.risk_disclosure.content.map((content, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleInputChange("risk_disclosure", "content", e.target.value, index)
                              }
                              placeholder="Enter Content"
                              rows="3"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("risk_disclosure", "content", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("risk_disclosure", "content", "")}
                      >
                        Add Risk Disclosure Content
                      </button>
                    </div>

                    <div className="form-group mb-3">
                      <label className="font-weight-bold text-dark mb-2">Risk Disclosure List</label>
                      <div id="risk_disclosure_list_container" className="mb-2">
                        {formData.risk_disclosure.list.map((item, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <span className="mr-2 font-weight-bold text-muted">{index + 1}.</span>
                            <textarea
                              className="form-control"
                              value={item}
                              onChange={(e) =>
                                handleInputChange("risk_disclosure", "list", e.target.value, index)
                              }
                              placeholder="Enter List"
                              rows="3"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-danger remove-field shadow-none ml-2"
                                onClick={() => removeArrayItem("risk_disclosure", "list", index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm shadow-none mt-1"
                        onClick={() => addArrayItem("risk_disclosure", "list", "")}
                      >
                        Add Risk Disclosure List
                      </button>
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-bold text-dark mb-2">Risk Disclosure Additional List</label>
                      <div id="additional-list-container">
                        {formData.risk_disclosure.additionalList.map((section, sectionIndex) => (
                          <div key={sectionIndex} className="section-block border p-3 mb-3 bg-white rounded">
                            <div className="form-group mb-3">
                              <label className="font-weight-bold text-muted mb-2">Title</label>
                              <input
                                type="text"
                                className="form-control"
                                value={section.title}
                                onChange={(e) =>
                                  handleInputChange("risk_disclosure", "additionalList", e.target.value, sectionIndex, "title")
                                }
                                placeholder="Enter title"
                              />
                            </div>

                            <div className="content-container mb-2">
                              {Array.isArray(section.content) &&
                                section.content.map((content, contentIndex) => (
                                  <div key={contentIndex} className="d-flex align-items-center mb-2">
                                    <span className="mr-2 font-weight-bold text-muted">{contentIndex + 1}.</span>
                                    <textarea
                                      className="form-control"
                                      value={content}
                                      onChange={(e) => {
                                        const newAdditionalList = [...formData.risk_disclosure.additionalList];
                                        newAdditionalList[sectionIndex].content[contentIndex] = e.target.value;
                                        setFormData((prev) => ({
                                          ...prev,
                                          risk_disclosure: {
                                            ...prev.risk_disclosure,
                                            additionalList: newAdditionalList,
                                          },
                                        }));
                                      }}
                                      placeholder="Enter content"
                                      rows="2"
                                    />
                                    {contentIndex > 0 && (
                                      <button
                                        type="button"
                                        className="btn btn-danger remove-content ml-2"
                                        onClick={() => removeAdditionalListContent(sectionIndex, contentIndex)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <button
                                type="button"
                                className="btn btn-info btn-sm"
                                onClick={() => addAdditionalListContent(sectionIndex)}
                              >
                                Add Content Point
                              </button>

                              {sectionIndex > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeAdditionalListSection(sectionIndex)}
                                >
                                  Remove Section
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        id="add-section"
                        className="btn btn-success btn-sm mt-2"
                        onClick={addAdditionalListSection}
                      >
                        Add Section
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="d-flex justify-content-end align-items-center mb-4 py-3">
            <button
              type="button"
              className="btn btn-secondary px-4 py-2 font-weight-bold"
              style={{ borderRadius: "6px" }}
              onClick={() => navigate("/research/Portfolio")}
              disabled={saving}
            >
              Cancel
            </button>
            <PermissionWrapper module="portfolioinfo" action="edit">
              <button
                type="submit"
                className="btn btn-primary ml-3 px-4 py-2 font-weight-bold"
                style={{ borderRadius: "6px", backgroundColor: "#3b82f6", borderColor: "#3b82f6" }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </PermissionWrapper>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortfolioAddPage;
